/**
 * Pure locale-detection logic — extracted so we can unit-test it without
 * spinning up Vike's runtime.
 *
 * Priority chain (server-side, since SSR cannot read localStorage):
 *   1. URL prefix       (always wins — canonical, shareable)
 *   2. Cookie           (set by switcher or client reconciler)
 *   3. Accept-Language  (q-weighted; full tag matches first, then primary subtag)
 *   4. Default          ("en")
 *
 * localStorage is reconciled client-side in `components/i18n/LocaleReconciler.tsx`.
 */

import {
  DEFAULT_LOCALE,
  LOCALE_COOKIE,
  LOCALES,
  isLocaleCode,
  resolveSegment,
  type LocaleCode,
} from "./locales.js";

export type LocaleSource = "url" | "cookie" | "accept-language" | "default";

export interface ResolveLocaleInput {
  /** Full request pathname (e.g. `/ar/outbreak/foo`). */
  urlPathname: string;
  /** Raw cookie header from the request (combined `name=value; name2=value2; …`). */
  cookieHeader?: string;
  /** Raw `Accept-Language` header from the request. */
  acceptLanguageHeader?: string;
}

export interface ResolveLocaleResult {
  /** Resolved canonical locale code. */
  locale: LocaleCode;
  /** Which layer of the priority chain produced the result. */
  source: LocaleSource;
  /** Pathname with the locale prefix stripped (e.g. `/outbreak/foo`). */
  urlLogical: string;
}

/**
 * Parse a single named cookie out of a raw `Cookie:` header.
 * Returns `null` when absent or when the header itself is undefined.
 */
export function parseCookie(
  header: string | undefined,
  name: string,
): string | null {
  if (!header) return null;
  for (const part of header.split(";")) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const eqIdx = trimmed.indexOf("=");
    if (eqIdx === -1) continue;
    const k = trimmed.slice(0, eqIdx);
    const v = trimmed.slice(eqIdx + 1);
    if (k === name) {
      try {
        return decodeURIComponent(v);
      } catch {
        return v;
      }
    }
  }
  return null;
}

/**
 * Pick the best supported locale from an `Accept-Language` header.
 *
 * Algorithm:
 *  - Parse comma-separated entries, capture optional `q=` quality factor.
 *  - Sort by quality (desc, stable for ties).
 *  - For each tag, try full-segment match first (`zh-cn` beats `zh`).
 *  - Fall back to primary-subtag fuzzy match (`zh` → `zh-CN` if supported).
 *  - Return `DEFAULT_LOCALE` when nothing matches.
 */
export function pickFromAcceptLanguage(header: string | undefined): LocaleCode {
  if (!header) return DEFAULT_LOCALE;

  const entries = header
    .split(",")
    .map((raw) => {
      const [rawTag, ...params] = raw.trim().split(";");
      const tag = (rawTag ?? "").toLowerCase().trim();
      let q = 1;
      for (const p of params) {
        const trimmed = p.trim();
        if (trimmed.startsWith("q=")) {
          const parsed = parseFloat(trimmed.slice(2));
          if (!Number.isNaN(parsed)) q = parsed;
        }
      }
      return { tag, q };
    })
    .filter((e) => e.tag.length > 0)
    .sort((a, b) => b.q - a.q);

  for (const { tag } of entries) {
    // Full-tag match (e.g. "zh-cn" → "zh-CN").
    const direct = LOCALES.find((l) => l.urlSegment === tag);
    if (direct) return direct.code;
    // Primary subtag fallback (e.g. "zh" → "zh-CN", "en-GB" → "en").
    const primary = tag.split("-")[0];
    if (!primary) continue;
    const fuzzy = LOCALES.find((l) => l.urlSegment.split("-")[0] === primary);
    if (fuzzy) return fuzzy.code;
  }

  return DEFAULT_LOCALE;
}

/**
 * Resolve the active locale for a request by walking the priority chain.
 *
 * Returned `urlLogical` is the pathname with the locale prefix stripped, so
 * downstream route matchers see a stable path regardless of whether the
 * locale arrived via URL or via cookie.
 */
export function resolveLocale(input: ResolveLocaleInput): ResolveLocaleResult {
  const { urlPathname, cookieHeader, acceptLanguageHeader } = input;
  const segments = urlPathname.split("/").filter(Boolean);
  const first = segments[0];

  // 1. URL prefix always wins.
  const urlMatch = resolveSegment(first);
  if (urlMatch) {
    const rest = `/${segments.slice(1).join("/")}`;
    return {
      locale: urlMatch,
      source: "url",
      urlLogical: rest === "/" ? "/" : rest,
    };
  }

  // 2. Cookie.
  const cookieVal = parseCookie(cookieHeader, LOCALE_COOKIE);
  if (cookieVal && isLocaleCode(cookieVal)) {
    return {
      locale: cookieVal,
      source: "cookie",
      urlLogical: urlPathname,
    };
  }

  // 3. Accept-Language.
  const accepted = pickFromAcceptLanguage(acceptLanguageHeader);
  return {
    locale: accepted,
    source: accepted === DEFAULT_LOCALE && !acceptLanguageHeader
      ? "default"
      : "accept-language",
    urlLogical: urlPathname,
  };
}
